import { useEffect, useState } from 'react'
import { Box, Card, IconButton, Toolbar, Grid, Divider, Typography, Paper, Chip, Avatar, Button, Tooltip, Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Switch, FormControlLabel } from '@mui/material'
import { ArrowBackIos, FiberManualRecord, Person, AccessTime, Delete, Add, Newspaper as NewspaperIcon, DoneAll, ExpandMore, Inbox, MarkChatRead, Archive, Restore, Visibility, Settings } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import WallPostEditorComponent from './WallPostEditor.component'
import NewsReadersList from './NewsReadersList.component'
import { newsRoutes, rolesRoutes, sitesRoutes } from '../../routes'
import { useStatus } from '../../context/Status.context'
import LoadingLogoDialog from '../../dialogs/LoadingLogoDialog'
import { dateWithTime } from '../../config'
import { useAuth, useUser } from '../../context'
import { useNotificationsContext } from '../../context/Notifications.context'
import { useUsersContext } from '../../context/Users.context'
import 'react-quill/dist/quill.snow.css'

const WallJournalPage = () => {
    const navigate = useNavigate()
    const { userData } = useAuth()
    const { admin, isSuperAdmin } = useUser()
    const { usersMap } = useUsersContext()
    const { marcarNoticiaComoLeidaEnContexto, marcarTodasComoLeidasEnContexto, noticias } = useNotificationsContext()

    const getValidReadersCount = (readBy = []) => {
        const uniqueIds = Array.from(new Set((readBy || []).map(userId =>
            typeof userId === 'string' ? userId : userId?.$oid || userId?._id
        )))
        return uniqueIds.filter(id => !!usersMap[id]).length
    }
    const { setstatus } = useStatus()
    const [isCreating, setIsCreating] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [allNews, setAllNews] = useState([])
    const [noticiaSeleccionada, setNoticiaSeleccionada] = useState()
    const [showReaders, setShowReaders] = useState(false)
    const [showArchiveConfig, setShowArchiveConfig] = useState(false)
    const [roles, setRoles] = useState([])
    const [obras, setObras] = useState([])
    const [titulo, setTitulo] = useState('')
    const [comentario, setComentario] = useState('')
    const [rolesSeleccionadosAenvio, setRolesSeleccionadosAenvio] = useState([])
    const [obrasSeleccionadasAenvio, setObrasSeleccionadasAenvio] = useState([])
    const [video, setVideo] = useState()
    const [imagen, setImagen] = useState()
    const [config, setConfig] = useState({
        targetType: 'users',
        isVersionNote: false,
        subtitle: '',
        createdBy: ''
    })

    const [openConfigDialog, setOpenConfigDialog] = useState(false)
    const [archiveConfig, setArchiveConfig] = useState({
        archiveDays: 15,
        autoArchive: true,
        archiveTime: '00:00'
    })

    const newsActive = allNews.filter(n => n.state !== false && !n.isRead)
    const newsRead = allNews.filter(n => n.state !== false && n.isRead)
    const newsArchived = allNews.filter(n => n.state === false)

    useEffect(() => {
        loadInitialData()
        if (admin || isSuperAdmin) {
            loadArchiveConfig()
        }
    }, [])

    const loadArchiveConfig = async () => {
        try {
            const res = await newsRoutes.getArchiveConfig()
            if (res.data && res.data.data) {
                setArchiveConfig(res.data.data)
            }
        } catch (error) {
            console.error('Error al cargar config de archivado:', error)
        }
    }

    const saveArchiveConfig = async () => {
        setLoadingData(true)
        try {
            const { preview, ...configToSave } = archiveConfig
            await newsRoutes.updateArchiveConfig(configToSave)
            setstatus({
                status: 'completed',
                title: 'Configuración Guardada',
                message: 'Los ajustes de archivado han sido actualizados.',
                severity: 'success'
            })
            loadArchiveConfig()
        } catch (error) {
            setstatus({ status: 'error', title: 'Error', message: 'No se pudo guardar la configuración' })
        } finally {
            setLoadingData(false)
        }
    }

    // Sincronizar el estado de lectura desde el contexto global
    useEffect(() => {
        if (allNews.length > 0 && noticias.length > 0) {
            const updatedNews = allNews.map(n => {
                const globalNews = noticias.find(gn => gn._id === n._id)
                return globalNews ? { ...n, isRead: globalNews.isRead } : n
            })
            setAllNews(updatedNews)
        }
    }, [noticias])

    const loadInitialData = async () => {
        // setLoadingData(true)
        try {
            const [newsRes, rolesRes, sitesRes] = await Promise.all([
                newsRoutes.getAllNews(),
                rolesRoutes.getRoles(),
                sitesRoutes.getSites()
            ])
            const newsData = newsRes.data.data
            const newsWithReadStatus = newsData.map(n => {
                const readByArray = n.readBy || []
                const isReadInBackend = readByArray.some(userId =>
                    (typeof userId === 'string' ? userId : userId?.$oid || userId?._id) === userData?._id
                )
                const globalNews = noticias.find(gn => gn._id === n._id)
                const isReadInContext = globalNews ? globalNews.isRead : false
                return { ...n, isRead: isReadInBackend || isReadInContext }
            })

            setAllNews(newsWithReadStatus)
            setRoles(rolesRes.data)
            setObras(sitesRes.data.data)

            const firstActive = newsWithReadStatus.find(n => n.state !== false)
            if (firstActive && !noticiaSeleccionada) {
                setNoticiaSeleccionada(firstActive)
            }
        } catch (error) {
            console.error('Error cargando datos:', error)
            setstatus({ status: 'error', message: 'Error al cargar la gestión del mural' })
        } finally {
            // setLoadingData(false)
        }
    }

    const seleccionarNoticia = async (news) => {
        setNoticiaSeleccionada(news)
        setIsCreating(false)
        setIsEditing(false)
        setShowReaders(false)

        if (!news.isRead && userData?._id) {
            try {
                await newsRoutes.markAsRead(news._id, userData._id)
                marcarNoticiaComoLeidaEnContexto(news._id)
            } catch (error) {
                console.error('Error al marcar como leída:', error)
            }
        }
    }

    const marcarTodasComoLeidas = async () => {
        if (!userData?._id) return
        try {
            await newsRoutes.markAllAsRead(userData._id)
            marcarTodasComoLeidasEnContexto()
        } catch (error) {
            console.error('Error al marcar todas como leídas:', error)
        }
    }

    const unreadCount = newsActive.length

    const onConfigChange = (newConfig) => {
        setConfig(prev => ({ ...prev, ...newConfig }))
    }

    const rolesSeleccionados = (roles, titulo, comentario, obras) => {
        setTitulo(titulo)
        setComentario(comentario)
        setRolesSeleccionadosAenvio(roles)
        setObrasSeleccionadasAenvio(obras)
    }

    const enviarPost = async () => {
        if (config.targetType === 'all' || rolesSeleccionadosAenvio.length > 0 || obrasSeleccionadasAenvio.length > 0) {
            if (titulo.length > 0) {
                if (comentario.length > 0) {
                    setLoadingData(true)
                    try {
                        let urlFile = isEditing ? noticiaSeleccionada.urlFile : ''
                        let nameFile = isEditing ? noticiaSeleccionada.nameFile : ''

                        if (imagen) {
                            const uploadRes = await newsRoutes.uploadFile(imagen)
                            urlFile = uploadRes.data.data.url
                            nameFile = imagen.name
                        } else if (video) {
                            const uploadRes = await newsRoutes.uploadFile(video)
                            urlFile = uploadRes.data.data.url
                            nameFile = video.name
                        }

                        const newsData = {
                            title: titulo,
                            subtitle: config.subtitle,
                            content: comentario,
                            targetType: config.targetType,
                            isVersionNote: config.isVersionNote,
                            createdBy: config.createdBy || userData?._id,
                            roles: rolesSeleccionadosAenvio,
                            sites: obrasSeleccionadasAenvio,
                            urlFile,
                            nameFile
                        }

                        if (isEditing) {
                            const res = await newsRoutes.updateNews(noticiaSeleccionada._id, newsData)
                            if (res.data && res.data.data) {
                                setNoticiaSeleccionada(res.data.data)
                            }
                        } else {
                            await newsRoutes.createNews(newsData)
                        }

                        setstatus({
                            status: 'completed',
                            title: isEditing ? 'Publicación Actualizada' : 'Publicación Creada',
                            message: `Se ha ${isEditing ? 'actualizado' : 'publicado'}: ${titulo}`,
                            severity: 'success'
                        })
                        setIsCreating(false)
                        setIsEditing(false)
                        loadInitialData()
                    } catch (error) {
                        setstatus({ status: 'error', title: 'Error de Red', message: `No se pudo ${isEditing ? 'actualizar' : 'publicar'} la noticia` })
                    } finally {
                        setLoadingData(false)
                    }
                } else {
                    setstatus({ status: 'completed', severity: 'warning', title: 'Validación', message: 'Falta el contenido' })
                }
            } else {
                setstatus({ status: 'completed', severity: 'warning', title: 'Validación', message: 'Falta el título' })
            }
        } else {
            setstatus({ status: 'completed', severity: 'warning', title: 'Validación', message: 'Seleccione un destino' })
        }
    }

    const eliminarNoticia = async (e, newsId) => {
        e.stopPropagation()
        const newsItem = allNews.find(n => n._id === newsId)
        if (!window.confirm(`¿Estás seguro de que deseas archivar "${newsItem?.title || newsItem?.titulo}"?`)) return

        setLoadingData(true)
        try {
            await newsRoutes.deleteNews(newsId)
            setstatus({
                status: 'completed',
                title: 'Nota archivada',
                message: newsItem?.title || newsItem?.titulo,
                severity: 'success'
            })
            loadInitialData()
            if (noticiaSeleccionada?._id === newsId) setNoticiaSeleccionada(undefined)
        } catch (error) {
            setstatus({ status: 'error', title: 'Error', message: 'No se pudo eliminar' })
        } finally {
            setLoadingData(false)
        }
    }

    const restaurarNoticia = async (e, newsId) => {
        e.stopPropagation()
        const newsItem = allNews.find(n => n._id === newsId)
        if (!window.confirm(`¿Deseas volver a mostrar la noticia "${newsItem?.title || newsItem?.titulo}"?`)) return

        setLoadingData(true)
        try {
            await newsRoutes.restoreNews(newsId)
            setstatus({
                status: 'completed',
                title: 'Nota restaurada',
                message: newsItem?.title || newsItem?.titulo,
                severity: 'success'
            })
            loadInitialData()
        } catch (error) {
            setstatus({ status: 'error', title: 'Error', message: 'No se pudo restaurar' })
        } finally {
            setLoadingData(false)
        }
    }

    const renderNewsItem = (news) => (
        <Paper
            key={news._id}
            elevation={0}
            onClick={() => seleccionarNoticia(news)}
            sx={{
                p: 1.5,
                mb: 1,
                cursor: 'pointer',
                borderRadius: 2,
                transition: 'all 0.2s',
                position: 'relative',
                border: (noticiaSeleccionada?._id === news._id && !isCreating) ? '1px solid brown' : '1px solid #eee',
                bgcolor: (noticiaSeleccionada?._id === news._id && !isCreating) ? '#fff9f9' : (news.isVersionNote ? '#f0f7ff' : 'transparent'),
                opacity: news.state === false ? 0.7 : 1,
                '&:hover': { bgcolor: '#f9f9f9', borderColor: 'brown' }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box>
                        <Typography variant="caption" sx={{
                            color: news.state === false ? '#888' : (news.isVersionNote ? '#0288d1' : '#666'),
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            fontSize: 9,
                            mb: 0.5,
                            display: 'block'
                        }}>
                            {news.state === false ? 'Archivado' : (news.isVersionNote ? 'Nota de Versión' : 'Noticia')}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: news.isRead ? 400 : 700, pr: 3, fontSize: '0.8rem', lineHeight: 1.2 }}>
                            {news.title || news.titulo}
                        </Typography>
                    </Box>
                    {news.state !== false && !news.isRead && <FiberManualRecord sx={{ color: 'brown', fontSize: 10 }} />}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Box sx={{
                        visibility: (admin || isSuperAdmin) ? 'visible' : 'hidden',
                        width: 32,
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                    </Box>
                    {isSuperAdmin && (
                        <>
                            {news.state === false ? (
                                <Tooltip title="Restaurar">
                                    <IconButton size="small" onClick={(e) => restaurarNoticia(e, news._id)} sx={{ color: '#0288d1', p: 0.5, '&:hover': { bgcolor: '#e3f2fd' } }}>
                                        <Restore sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <Tooltip title="Archivar">
                                    <IconButton size="small" onClick={(e) => eliminarNoticia(e, news._id)} sx={{ color: '#ccc', p: 0.5, '&:hover': { color: 'red' } }}>
                                        <Delete sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </>
                    )}
                </Box>
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5, fontSize: 10 }}>
                <AccessTime sx={{ fontSize: 10, mr: 0.5 }} />
                {dateWithTime(news.createdAt)}
            </Typography>
        </Paper>
    )

    return (
        <Box sx={{ height: '100vh', bgcolor: '#f4f4f4', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <LoadingLogoDialog open={loadingData} />
            <Box sx={{ pt: 10, flexGrow: 1, overflow: 'hidden' }}>
                <Card elevation={0} sx={{ height: '100%', borderRadius: 0, display: 'flex', flexDirection: 'column' }}>

                    <Box sx={{ p: 2, bgcolor: '#fff', borderBottom: '1px solid #eee' }}>
                        <Toolbar variant="regular" sx={{ p: 0, justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton onClick={() => isCreating ? setIsCreating(false) : navigate(-1)} sx={{ mr: 1 }}>
                                    <ArrowBackIos style={{ fontSize: 18 }} />
                                </IconButton>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'brown' }}>
                                    {isCreating ? 'Nueva Publicación' : 'Mural Informativo'}
                                </Typography>
                            </Box>
                            {!isCreating && (
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    {unreadCount > 0 && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<DoneAll />}
                                            onClick={marcarTodasComoLeidas}
                                            sx={{ color: 'brown', borderColor: 'brown', borderRadius: 2, fontWeight: 700 }}
                                        >
                                            Leer Todo
                                        </Button>
                                    )}
                                    <Button
                                        variant="contained"
                                        startIcon={<Add />}
                                        onClick={() => setIsCreating(true)}
                                        sx={{ bgcolor: 'brown', borderRadius: 2, px: 3, py: 1, '&:hover': { bgcolor: '#5d0e0e' }, boxShadow: 2 }}
                                    >
                                        Publicar
                                    </Button>
                                </Box>
                            )}
                        </Toolbar>
                    </Box>

                    <Grid container sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        <Grid item xs={12} md={3} sx={{ borderRight: '1px solid #eee', bgcolor: '#f8f9fa', height: '100%', overflowY: 'auto' }}>
                            <Box sx={{ p: 0 }}>
                                {/* SECCIÓN ACTIVAS */}
                                <Accordion defaultExpanded elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Inbox sx={{ color: 'brown', fontSize: 20 }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Pendientes ({newsActive.length})</Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 1, pt: 0 }}>
                                        {newsActive.map(renderNewsItem)}
                                        {newsActive.length === 0 && (
                                            <Typography variant="caption" sx={{ textAlign: 'center', display: 'block', py: 2, color: '#999' }}>
                                                No hay noticias pendientes
                                            </Typography>
                                        )}
                                    </AccordionDetails>
                                </Accordion>

                                {/* SECCIÓN LEÍDAS / HISTORIAL */}
                                <Accordion elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <MarkChatRead sx={{ color: '#666', fontSize: 20 }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Historial ({newsRead.length})</Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 1, pt: 0 }}>
                                        {newsRead.map(renderNewsItem)}
                                    </AccordionDetails>
                                </Accordion>

                                {/* SECCIÓN ARCHIVADAS (Sólo Admin) */}
                                {newsArchived.length > 0 && (
                                    <Accordion elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Archive sx={{ color: '#888', fontSize: 20 }} />
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Archivo ({newsArchived.length})</Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 1, pt: 0 }}>
                                            {newsArchived.map(renderNewsItem)}
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={(!isCreating && noticiaSeleccionada && (admin || isSuperAdmin)) ? 6 : 9} sx={{ height: '100%', overflowY: 'auto', bgcolor: isCreating ? '#fff' : (noticiaSeleccionada?.isVersionNote ? '#f0f4f8' : '#fcfcfc') }}>
                            {isCreating ? (
                                <WallPostEditorComponent
                                    roles={roles} rolesSeleccionados={rolesSeleccionados} obras={obras}
                                    setVideo={setVideo} setImagen={setImagen} onConfigChange={onConfigChange}
                                    onSend={enviarPost} loading={loadingData}
                                    initialData={isEditing ? noticiaSeleccionada : null}
                                />
                            ) : noticiaSeleccionada ? (
                                <Box sx={{
                                    p: { xs: 2, md: 4 },
                                    width: '100%',
                                    bgcolor: noticiaSeleccionada.isVersionNote ? '#fff' : 'transparent',
                                    minHeight: '100%',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <Chip
                                            label={noticiaSeleccionada.isVersionNote ? 'Nota de Versión' : 'Noticia'}
                                            size="small"
                                            sx={{ fontWeight: 700, bgcolor: noticiaSeleccionada.isVersionNote ? '#0288d1' : '#eee', color: noticiaSeleccionada.isVersionNote ? '#fff' : '#666' }}
                                        />
                                        {noticiaSeleccionada.state === false && (
                                            <Chip label="Archivado" size="small" variant="outlined" color="error" sx={{ fontWeight: 700 }} />
                                        )}
                                    </Box>

                                    <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: '#1a1a1a', lineHeight: 1.2 }}>
                                        {noticiaSeleccionada.title || noticiaSeleccionada.titulo}
                                    </Typography>

                                    {noticiaSeleccionada.subtitle && (
                                        <Typography variant="h6" sx={{ color: '#666', mb: 1, fontWeight: 400 }}>
                                            {noticiaSeleccionada.subtitle}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mb: 3, opacity: 0.8 }}>
                                        <AccessTime sx={{ fontSize: 14, mr: 0.5 }} />
                                        Publicado el {dateWithTime(noticiaSeleccionada.createdAt)}
                                    </Typography>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4, mt: 2, justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: 'brown' }}><Person /></Avatar>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                    {(() => {
                                                        const creator = noticiaSeleccionada.createdBy
                                                        if (!creator) return 'Sistema'
                                                        const roleDbName = typeof creator.role === 'object' ? creator.role?.dbName : creator.role
                                                        if (roleDbName === 'superAdmin') return 'Sistema'
                                                        return creator.name ? `${creator.name} ${creator.lastName}`.trim() : 'Sistema'
                                                    })()}
                                                </Typography>
                                                {noticiaSeleccionada.createdBy?.role && (() => {
                                                    const roleDbName = typeof noticiaSeleccionada.createdBy.role === 'object'
                                                        ? noticiaSeleccionada.createdBy.role?.dbName
                                                        : noticiaSeleccionada.createdBy.role
                                                    return roleDbName !== 'superAdmin'
                                                })() && (
                                                    <Typography variant="caption" sx={{ color: 'brown', fontWeight: 600, display: 'block', mt: -0.5 }}>
                                                        {noticiaSeleccionada.createdBy.role.name}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box sx={{
                                            visibility: (admin || isSuperAdmin) ? 'visible' : 'hidden',
                                            minWidth: 140,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-end',
                                            gap: 1
                                        }}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                {isSuperAdmin && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => {
                                                            setIsEditing(true)
                                                            setIsCreating(true)
                                                        }}
                                                        sx={{ color: 'brown', borderColor: 'brown', '&:hover': { bgcolor: '#fff9f9' } }}
                                                    >
                                                        Editar
                                                    </Button>
                                                )}
                                            </Box>
                                            <Button
                                                size="small"
                                                variant={showReaders ? 'contained' : 'outlined'}
                                                startIcon={<Visibility />}
                                                onClick={() => setShowReaders(!showReaders)}
                                                sx={{
                                                    borderRadius: 2,
                                                    bgcolor: showReaders ? 'brown' : 'transparent',
                                                    color: showReaders ? '#fff' : 'brown',
                                                    borderColor: 'brown',
                                                    '&:hover': { bgcolor: showReaders ? '#5d0e0e' : '#fff9f9', borderColor: 'brown' }
                                                }}
                                            >
                                                {showReaders ? 'Ocultar Lecturas' : `Vistos (${getValidReadersCount(noticiaSeleccionada.readBy)})`}
                                            </Button>
                                        </Box>
                                    </Box>

                                    {(admin || isSuperAdmin) && (
                                        <Box sx={{ mb: 4, p: 2, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px dashed #ddd' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#888', display: 'block', mb: 1, textTransform: 'uppercase' }}>
                                                Segmentación de Envío
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                <Chip
                                                    label={`Tipo: ${noticiaSeleccionada.targetType === 'all' ? 'General'
                                                        : noticiaSeleccionada.targetType === 'filter' ? 'Roles y Sitios'
                                                            : noticiaSeleccionada.targetType === 'roles' ? 'Por Roles'
                                                                : noticiaSeleccionada.targetType === 'sites' ? 'Por Sitios' : 'Usuarios Específicos'}`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ fontWeight: 700 }}
                                                />

                                                {noticiaSeleccionada.roles?.length > 0 && noticiaSeleccionada.roles.map(r => (
                                                    <Chip
                                                        key={r}
                                                        label={`Rol: ${roles.find(role => role.dbName === r)?.name || r}`}
                                                        size="small"
                                                        sx={{ bgcolor: '#efebe9', fontWeight: 600 }}
                                                    />
                                                ))}

                                                {noticiaSeleccionada.sites?.length > 0 && noticiaSeleccionada.sites.map(s => {
                                                    const siteLabel = typeof s === 'object'
                                                        ? `${s.idObra || ''} - ${s.descripcion || ''}`.trim()
                                                        : `ID: ${s}`

                                                    return (
                                                        <Chip
                                                            key={s._id || s}
                                                            label={`Sitio: ${siteLabel}`}
                                                            size="small"
                                                            sx={{ bgcolor: '#e3f2fd', fontWeight: 600 }}
                                                        />
                                                    )
                                                })}

                                                {noticiaSeleccionada.targetType === 'users' && noticiaSeleccionada.users?.length > 0 && (
                                                    <Chip label={`${noticiaSeleccionada.users.length} Usuarios específicos`} size="small" variant="outlined" />
                                                )}
                                            </Box>
                                        </Box>
                                    )}

                                    <Divider sx={{ mb: 4 }} />

                                    <Box sx={{ flexGrow: 1 }}>
                                        {(noticiaSeleccionada.urlFile || noticiaSeleccionada.urlVideo || noticiaSeleccionada.urlFoto) && (
                                            <Box sx={{ mb: 4, borderRadius: 3, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', maxWidth: 1000 }}>
                                                {noticiaSeleccionada.urlVideo ? (
                                                    <video controls src={noticiaSeleccionada.urlVideo} width="100%" style={{ display: 'block' }} />
                                                ) : (
                                                    <img src={noticiaSeleccionada.urlFile || noticiaSeleccionada.urlFoto} alt="news" style={{ width: '100%', display: 'block' }} />
                                                )}
                                            </Box>
                                        )}
                                        <div
                                            className="ql-editor ql-content"
                                            dangerouslySetInnerHTML={{ __html: noticiaSeleccionada.content || noticiaSeleccionada.comentario }}
                                            style={{
                                                fontSize: '1.1rem',
                                                lineHeight: 1.8,
                                                color: '#333',
                                                fontFamily: 'Georgia, serif',
                                                padding: 0
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', opacity: 0.5 }}>
                                    <NewspaperIcon sx={{ fontSize: 80, mb: 2, color: '#ccc' }} />
                                    <Typography variant="h6">No hay noticias seleccionadas</Typography>
                                </Box>
                            )}
                        </Grid>

                        {(!isCreating && noticiaSeleccionada && (admin || isSuperAdmin) && (showReaders || showArchiveConfig)) && (
                            <Grid item xs={12} md={3} sx={{ height: '100%', bgcolor: '#fff', borderLeft: '1px solid #eee', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ flexGrow: 1, overflowY: 'auto', display: showReaders ? 'block' : 'none' }}>
                                        <NewsReadersList readBy={noticiaSeleccionada.readBy} />
                                    </Box>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Card>
            </Box>
        </Box>
    )
}

export default WallJournalPage
